import {
  afterNextRender,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  animate,
  query,
  stagger,
  style,
  transition,
  trigger,
} from '@angular/animations';
import {
  AnnouncementInvitedJobseeker,
  AnnouncementInviteResponseEnum,
  AnnouncementListingSectionEnum,
  AnnouncementTypeEnum,
  ApplicationMethodEnum,
  UserTypeEnum,
  WorkExperiencTypeEnum,
} from '@hrra/common-models';
import { SsrService } from '@hrra/core';

@Component({
  selector: 'hrra-announcement-details-tile',
  templateUrl: './redesign/announcement-details-tile.component.html',
  styleUrls: ['./redesign/announcement-details-tile.component.scss'],
  standalone: false,
  animations: [
    trigger('listAnimation', [
      transition(':enter', [
        query(
          'div',
          [
            style({ opacity: 0, transform: 'translateY(-10px)' }),
            stagger(100, [
              animate(
                '300ms ease-out',
                style({ opacity: 1, transform: 'translateY(0)' })
              ),
            ]),
          ],
          { optional: true }
        ),
      ]),
      transition(':leave', [
        animate(
          '200ms ease-in',
          style({ opacity: 0, transform: 'translateY(-10px)' })
        ),
      ]),
    ]),
    trigger('copiedAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateX(-50%) translateY(10px)' }),
        animate(
          '250ms ease-out',
          style({ opacity: 1, transform: 'translateX(-50%) translateY(0)' })
        ),
      ]),
      transition(':leave', [
        animate(
          '250ms ease-in',
          style({ opacity: 0, transform: 'translateX(-50%) translateY(-5px)' })
        ),
      ]),
    ]),
  ],
})
export class AnnouncementDetailsTileComponent implements OnChanges {
  @Input() announcementDetails: any;
  @Input() publishDate: Date;
  @Input() timeZone: string;
  @Input() currentLanguage: string;
  @Input() currentUserType: UserTypeEnum;
  @Input() user: any;
  @Input() isSignedIn: boolean;
  @Input() isJobseekerSignedIn: boolean;
  @Input() isFavorite: boolean;
  @Input() isAnnouncementLinkCopied: boolean;
  @Input() socialMediaShareLinks: any;
  @Input() providerWebsiteId: any;

  // Invitation response section: shown instead of the regular buttons when the
  // tile is opened from the jobseeker invitations page.
  @Input() showInviteResponse = false;

  // Public details page (HR-3762): unlike the dedicated invitations page, when a
  // signed-in jobseeker is invited we still keep the apply / favourite / share
  // actions alongside the invite-response buttons.
  @Input() isPublicView = false;

  @Input() isJobseekerInvited!: boolean;
  @Input() announcementInvitedJobseeker!: AnnouncementInvitedJobseeker;

  @Output() cvSend = new EventEmitter<number>();
  @Output() toggleFavorite = new EventEmitter<boolean>();
  @Output() externalApply = new EventEmitter<string>();
  @Output() copyLink = new EventEmitter<void>();
  @Output() socialShare = new EventEmitter<string>();
  @Output() inviteRespond = new EventEmitter<{responseStatus: AnnouncementInviteResponseEnum, inviteId: number, applicationMethod: ApplicationMethodEnum}>();

  public isShareButtonClicked = false;
  public isShareButtonClickedResponsive = false;

  // Edit mode: entered from a chosen status that can still be upgraded; only
  // statuses higher than the current one are selectable while editing.
  public isInviteEditMode = false;
  
  public isBrowser: boolean;
  

  constructor(private cdr: ChangeDetectorRef, public ssr: SsrService) {
    this.isBrowser = this.ssr.isPlatformBrowser();
  }

  public applicationMethod: any = ApplicationMethodEnum;
  public userType: any = UserTypeEnum;
  public workExperienceTypeEnum: any = WorkExperiencTypeEnum;
  public announcementType: any = AnnouncementTypeEnum;
  public inviteResponse: any = AnnouncementInviteResponseEnum;

  public listingSectionNameMap = {
    [AnnouncementListingSectionEnum.VipCV]: 'label.VipCv',
    [AnnouncementListingSectionEnum.Vip]: 'label.Vip',
    [AnnouncementListingSectionEnum.Exclusive]: 'label.Exclusive',
    [AnnouncementListingSectionEnum.P2]: 'label.PrioStandard',
    [AnnouncementListingSectionEnum.P1]: 'label.PrioStart',
  };

  public announcementWorkExperienceEnumMap: { [key: number]: string } = {
    1: '',
    2: 'text.WithoutExperience',
    3: 'text.WithExperience',
  };

  public inviteResponseLabelMap: { [key: number]: string } = {
    [AnnouncementInviteResponseEnum.Interested]:
      'label.InvitationStatusInterested',
    [AnnouncementInviteResponseEnum.InterestedAndApplied]:
      'label.InvitationStatusInterestedAndApply',
    [AnnouncementInviteResponseEnum.NotInterested]:
      'label.InvitationStatusNotInterested',
  };

  public inviteResponseModifierMap: { [key: number]: string } = {
    [AnnouncementInviteResponseEnum.Interested]: 'interested',
    [AnnouncementInviteResponseEnum.InterestedAndApplied]:
      'interested-and-apply',
    [AnnouncementInviteResponseEnum.NotInterested]: 'not-interested',
  };

  // Upgrade order of invite responses (low -> high). A chosen response can only
  // be changed to a higher one; the current and lower options are disabled.
  public inviteResponseRankMap: { [key: number]: number } = {
    [AnnouncementInviteResponseEnum.NotInterested]: 0,
    [AnnouncementInviteResponseEnum.Interested]: 1,
    [AnnouncementInviteResponseEnum.InterestedAndApplied]: 2,
    [AnnouncementInviteResponseEnum.NotResponded]: -1,
  };

  private get isViaWebsiteApplication(): boolean {
    return (
      this.announcementDetails?.applicationMethod ===
      ApplicationMethodEnum.ViaWebsite
    );
  }

  private inviteRank(status: AnnouncementInviteResponseEnum): number {
    return this.inviteResponseRankMap[status];
  }

  public get isInviteStatusChosen(): boolean {
    const responseStatus = this.announcementInvitedJobseeker?.responseStatus;
    return (
      responseStatus != null &&
      responseStatus !== AnnouncementInviteResponseEnum.NotResponded &&
      !this.isInviteEditMode
    );
  }

  // A chosen response can be edited only when a higher status exists to
  // upgrade to. "Not interested" can always be upgraded; "Interested" can only
  // be upgraded to "Interested and apply", which exists for website
  // applications, so it is not editable for other application methods.
  public get canEditInviteResponse(): boolean {
    if (!this.isInviteStatusChosen) {
      return false;
    }
    const status = this.announcementInvitedJobseeker?.responseStatus;
    if (status === AnnouncementInviteResponseEnum.NotInterested) {
      return true;
    }
    if (status === AnnouncementInviteResponseEnum.Interested) {
      return this.isViaWebsiteApplication;
    }
    return false;
  }

  // While editing, only lower statuses are disabled (no downgrades). The current
  // status stays selectable and re-choosing it changes nothing / sends no
  // request; higher statuses are selectable as an upgrade.
  public isInviteOptionDisabled(
    status: AnnouncementInviteResponseEnum
  ): boolean {
    if (!this.isInviteEditMode) {
      return false;
    }
    const current = this.announcementInvitedJobseeker?.responseStatus;
    return this.inviteRank(status) < this.inviteRank(current);
  }

  public get isEditedVisible(): boolean {
    const moderationLastChangeDate =
      this.announcementDetails?.moderationLastChangeDate;
    if (!moderationLastChangeDate) {
      return false;
    }

    const inviteDate = this.announcementInvitedJobseeker?.createDate;
    if (!inviteDate) {
      return false;
    }

    return new Date(inviteDate) < new Date(moderationLastChangeDate);
  }

  public onCvSend() {
    this.cvSend.emit(this.announcementDetails?.announcementId);
  }

  public onToggleFavorite() {
    this.toggleFavorite.emit(this.isFavorite === true);
  }

  public onExternalApply() {
    this.externalApply.emit(
      this.announcementDetails?.applicationDetails?.applicationUrl
    );
  }

  public onCopyLink() {
    this.copyLink.emit();
  }

  public onSocialShare(socMediaUrlTemplate: string) {
    this.socialShare.emit(socMediaUrlTemplate);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const change = changes['announcementInvitedJobseeker'];
    if (
      change &&
      !change.firstChange &&
      change.previousValue?.responseStatus !== change.currentValue?.responseStatus
    ) {
      this.isInviteEditMode = false;
    }
  }

  public onInviteRespond(responseStatus: AnnouncementInviteResponseEnum) {
    if (responseStatus === this.announcementInvitedJobseeker?.responseStatus) {
      // Re-choosing the current status opens no dialog, so leave edit mode
      // immediately just like before the confirm dialog was introduced.
      this.isInviteEditMode = false;
      return;
    }
    this.inviteRespond.emit({responseStatus:responseStatus, inviteId: this.announcementInvitedJobseeker.announcementInvitedJobseekerId, applicationMethod: this.announcementDetails?.applicationMethod});
  }

  public onEnableInviteEdit() {
    this.isInviteEditMode = true;
  }

  public onToggleShare() {
    this.isShareButtonClicked = !this.isShareButtonClicked;
  }

  public onToggleShareResponsive() {
    this.isShareButtonClickedResponsive = !this.isShareButtonClickedResponsive;
  }
}
